import { Component, MouseEvent, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { Popper, Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { PoolButton } from '../button/pool-button';

const styles = (theme: Theme) =>
    createStyles({
        btnGroup: {
            display: 'inline-flex',
            alignItems: 'center',

            '&:hover button': {
                backgroundColor: '#F1F1F1',
            },

            '& button:first-child': {
                paddingRight: theme.spacing(),
                borderTopLeftRadius: theme.spacing(3),
                borderBottomLeftRadius: theme.spacing(3),
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                fontSize: '15px', // screendesign

                '& > svg': {
                    marginRight: theme.spacing(),
                },
            },

            '& button:last-child': {
                paddingLeft: 0,
                paddingRight: theme.spacing(),
                minWidth: 0,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: theme.spacing(3),
                borderBottomRightRadius: theme.spacing(3),
            },
        },
    });

interface PoolDropdownButtonProps extends WithStyles<typeof styles> {
    label: ReactNode;
    popperContent: () => ReactElement<ReactNode>; // FIXME: Don't know if I did those typings correctly
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    placement?:
        | 'bottom-end'
        | 'bottom-start'
        | 'bottom'
        | 'left-end'
        | 'left-start'
        | 'left'
        | 'right-end'
        | 'right-start'
        | 'right'
        | 'top-end'
        | 'top-start'
        | 'top';
}

interface PoolDropdownButtonState {
    open: boolean;
}

export const PoolDropdownButton = withStyles(styles)(
    class PoolDropdownButtonBase extends Component<
        PropsWithChildren<PoolDropdownButtonProps>,
        PoolDropdownButtonState
    > {
        private anchorNode: HTMLDivElement | null = null;
        private contentNode: HTMLDivElement | null = null;

        public override state = {
            open: false,
        };

        private toggleOpen = () => {
            this.setState(state => {
                return {
                    open: !state.open,
                };
            });
        };

        public handleClick = (event: globalThis.MouseEvent) => {
            const isChild = this.contentNode && event.target && this.contentNode.contains(event.target as Node);
            if (isChild) {
                return;
            }
            this.setState({ open: false });
        };

        public override componentDidMount() {
            document.addEventListener('mousedown', this.handleClick, false);
        }

        public override componentWillUnmount() {
            document.removeEventListener('mousedown', this.handleClick, false);
        }

        public renderContent = () => {
            return (
                <div ref={node => (this.contentNode = node)}>
                    {this.props.children || this.props.popperContent() || null}
                </div>
            );
        };

        public override render() {
            const { classes, placement } = this.props;

            return (
                <div>
                    <div className={classes.btnGroup} ref={node => (this.anchorNode = node)}>
                        <PoolButton onClick={this.props.onClick}>
                            <AddIcon color="primary" />
                            {this.props.label}
                        </PoolButton>
                        <PoolButton onClick={this.toggleOpen}>
                            <ArrowDropDownIcon />
                        </PoolButton>
                    </div>
                    <Popper
                        placement={placement || 'bottom-end'}
                        disablePortal={false}
                        modifiers={[
                            {
                                name: 'preventOverflow',
                                enabled: false,
                                options: {
                                    boundariesElement: 'scrollParent',
                                },
                            },
                            {
                                name: 'flip',
                                enabled: true,
                            },
                            {
                                name: 'hide',
                                enabled: false,
                            },
                        ]}
                        anchorEl={this.anchorNode || undefined}
                        open={this.state.open}
                        children={this.renderContent}
                    />
                </div>
            );
        }
    }
);
